// src/pages/novotechnik/Novotechnik.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import './Novotechnik.css';

const Novotechnik = () => {
    // --- Estados existentes (sem alteração) ---
    const [allCategories, setAllCategories] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentView, setCurrentView] = useState('list');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [activeImage, setActiveImage] = useState('');
    const [quantity, setQuantity] = useState(1);

    // --- NOVOS ESTADOS PARA FILTROS E PAGINAÇÃO ---
    const [activeFilters, setActiveFilters] = useState({});
    const [availableFilters, setAvailableFilters] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const productsPerPage = 10;

    // --- LÓGICA DE BUSCA DE DADOS (sem alteração) ---
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const categoriesRef = collection(db, "pts-com-br", "fabricantes", "novotechnik_categories");
                const categoriesQuery = query(categoriesRef, orderBy("name"));
                const categorySnapshot = await getDocs(categoriesQuery);
                const categoriesData = categorySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                if (categoriesData.length > 0) {
                    setAllCategories(categoriesData);
                    setSelectedCategoryId(categoriesData[0].id);
                } else {
                    throw new Error("Nenhuma categoria encontrada.");
                }

                const productsRef = collection(db, "pts-com-br", "fabricantes", "novotechnik_products");
                const productSnapshot = await getDocs(productsRef);
                setAllProducts(productSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // --- LÓGICA DE FILTROS (NOVA) ---
    // Gera a lista de filtros disponíveis a partir dos produtos da categoria
    useEffect(() => {
        const productsInCategory = allProducts.filter(p => p.categoryId === selectedCategoryId);
        const newAvailableFilters = {};
        const filterKeys = ['cursoUtil', 'tecnologia', 'aplicacao', 'interface', 'resolucao', 'protecao'];
        
        filterKeys.forEach(key => {
            const values = [...new Set(productsInCategory.map(p => p[key]).filter(Boolean))];
            if (values.length > 1) { // Só mostra o filtro se houver mais de uma opção
                newAvailableFilters[key] = values.sort();
            }
        });
        setAvailableFilters(newAvailableFilters);
        setActiveFilters({}); 
        setCurrentPage(1); 
    }, [selectedCategoryId, allProducts]);

    // Aplica os filtros e a seleção de categoria aos produtos
    const filteredProducts = useMemo(() => {
        let products = allProducts.filter(p => p.categoryId === selectedCategoryId);

        Object.entries(activeFilters).forEach(([key, value]) => {
            if (value) {
                products = products.filter(p => p[key] === value);
            }
        });
        return products;
    }, [allProducts, selectedCategoryId, activeFilters]);

    // --- LÓGICA DE PAGINAÇÃO (NOVA) ---
    const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
    const paginatedProducts = useMemo(() => {
        return filteredProducts.slice((currentPage - 1) * productsPerPage, currentPage * productsPerPage);
    }, [filteredProducts, currentPage, productsPerPage]);

    // --- FUNÇÕES DE CONTROLE (NOVAS E EXISTENTES) ---
    const handleFilterChange = (key, value) => {
        setActiveFilters(prev => ({ ...prev, [key]: activeFilters[key] === value ? null : value }));
        setCurrentPage(1); 
    };

    const handleSelectCategory = (categoryId) => {
        setSelectedCategoryId(categoryId);
        handleBackToList(); // Garante que sempre volte para a lista ao mudar de categoria
    };
    
    // Funções existentes sem alteração
    const handleSelectProduct = (product) => {
        setSelectedProduct(product);
        setActiveImage(product.mainImage);
        setCurrentView('detail');
    };
    const handleBackToList = () => {
        setSelectedProduct(null);
        setCurrentView('list');
    };
    const handleAddToCart = () => {
        alert(`${quantity}x "${selectedProduct.name}" adicionado(s) à cotação!`);
    };

    const currentCategory = allCategories.find(c => c.id === selectedCategoryId);

    if (loading) return <div className="loading-container container">Carregando...</div>;
    if (error) return <div className="error-container container"><h1>Erro</h1><p>{error}</p></div>;

    return (
        <div className="product-page-container">
            <div className="product-layout">
                {/* --- SIDEBAR ATUALIZADA COM FILTROS --- */}
                <aside className="product-sidebar">
                    <div className="sidebar-section">
                        <h3>Categorias</h3>
                        <ul className="category-list">
                            {allCategories.map(cat => (
                                <li key={cat.id} className={selectedCategoryId === cat.id ? 'active' : ''} onClick={() => handleSelectCategory(cat.id)}>
                                    {cat.name} ({cat.productCount || 0})
                                </li>
                            ))}
                        </ul>
                    </div>
                    {Object.entries(availableFilters).map(([key, values]) => (
                        <div key={key} className="sidebar-section">
                            <h3>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</h3>
                            <ul className="filter-list">
                                {values.map(value => (
                                    <li key={value} onClick={() => handleFilterChange(key, value)}>
                                        <input type="checkbox" readOnly checked={activeFilters[key] === value} />
                                        <label>{value}</label>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </aside>

                <main className="product-content">
                    {/* A lógica de renderização condicional entre 'list' e 'detail' permanece a mesma */}
                    {currentView === 'list' && (
                        <div className="product-list-view">
                            <div className="category-header">
                                <h2>{currentCategory?.name}</h2>
                            </div>
                            {/* --- TABELA DE PRODUTOS COM PAGINAÇÃO --- */}
                            <table className="product-list-table">
                                <thead>
                                    <tr>
                                        <th>Produto</th>
                                        <th>Resolução</th>
                                        <th>Proteção</th>
                                        <th>Aplicação</th>
                                        <th>Temperatura</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedProducts.map(prod => (
                                        <tr key={prod.id} onClick={() => handleSelectProduct(prod)} className="product-list-row">
                                            <td>{prod.name}</td>
                                            <td>{prod.resolucao || '-'}</td>
                                            <td>{prod.protecao || '-'}</td>
                                            <td>{prod.aplicacao || '-'}</td>
                                            <td>{prod.temperatura || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            
                            {totalPages > 1 && (
                                <div className="pagination-controls">
                                    <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>Anterior</button>
                                    <span>Página {currentPage} de {totalPages}</span>
                                    <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>Próxima</button>
                                </div>
                            )}

                            {/* --- DESCRIÇÃO DA CATEGORIA ADICIONADA AO FINAL --- */}
                            <div className="category-description-section">
                                {currentCategory?.mainMedia && <img src={currentCategory.mainMedia} alt={currentCategory.name} className="category-description-image" />}
                                <div className="richtext-content" dangerouslySetInnerHTML={{ __html: currentCategory?.description }} />
                            </div>
                        </div>
                    )}

                    {currentView === 'detail' && selectedProduct && (
                        // A sua visualização de detalhes do produto permanece exatamente a mesma
                        <div className="product-detail-view">
                            <button onClick={handleBackToList} className="back-to-list-button">
                                &larr; Voltar para a lista de produtos
                            </button>
                            <div className="product-detail-layout">
                                <div className="product-gallery">
                                    <img src={activeImage || 'https://placehold.co/500x500/eee/ccc?text=Sem+Imagem'} alt={selectedProduct.name} className="main-product-image" />
                                    <div className="thumbnail-container">
                                        {selectedProduct.mainImage && <img src={selectedProduct.mainImage} alt="Principal" onClick={() => setActiveImage(selectedProduct.mainImage)} className={activeImage === selectedProduct.mainImage ? 'active' : ''} />}
                                        {selectedProduct.imageGallery?.map((imgUrl, index) => (
                                            <img key={index} src={imgUrl} alt={`Thumbnail ${index + 1}`} onClick={() => setActiveImage(imgUrl)} className={activeImage === imgUrl ? 'active' : ''} />
                                        ))}
                                    </div>
                                </div>
                                <div className="product-info">
                                    <h2>{selectedProduct.name}</h2>
                                    <span className="product-sku">Cód: {selectedProduct.sku}</span>
                                    <p>{selectedProduct.shortDescription}</p>
                                    <div className="product-actions">
                                        <div className="quantity-selector">
                                            <label htmlFor="qty">Qtd:</label>
                                            <input type="number" id="qty" min="1" value={quantity} onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10)))} />
                                        </div>
                                        <button className="add-to-cart-button" onClick={handleAddToCart}>
                                            Adicionar à Cotação
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="product-full-details">
                                <h3>Descrição Completa</h3>
                                <div dangerouslySetInnerHTML={{ __html: selectedProduct.description }} />
                                <h3>Especificações Técnicas</h3>
                                {selectedProduct.technicalSpecs && Object.keys(selectedProduct.technicalSpecs).length > 0 ? (
                                    <ul>
                                        {Object.entries(selectedProduct.technicalSpecs).map(([key, value]) => (
                                            <li key={key}><strong>{key}:</strong> {value}</li>
                                        ))}
                                    </ul>
                                ) : <p>Não informado.</p>}
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default Novotechnik;